import React from "react";
import Shimmer from "./Shimmer";

/**
 * Generic page skeleton — mirrors typical user page layout.
 * Use for loading states across user-facing pages.
 */
const PageSkeleton = ({ variant = "default" }) => {
  if (variant === "orders") {
    return (
      <div className="max-w-2xl mx-auto px-4 pb-8 space-y-6 animate-pulse">
        <div className="mb-6">
          <Shimmer className="h-8 w-40 rounded mb-2" />
          <Shimmer className="h-4 w-56 rounded" />
        </div>
        <ul className="space-y-5">
          {[1, 2, 3].map((i) => (
            <li key={i} className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
              <div className="flex justify-between">
                <Shimmer className="h-4 w-24" />
                <Shimmer className="h-4 w-16" />
              </div>
              <div className="space-y-2">
                <Shimmer className="h-3 w-full rounded" />
                <Shimmer className="h-3 w-3/4 rounded" />
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-100">
                <Shimmer className="h-5 w-20" />
                <Shimmer className="h-5 w-14" />
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (variant === "favorites") {
    return (
      <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-6 animate-pulse">
        <div className="flex justify-between items-center">
          <Shimmer className="h-7 w-32 rounded" />
          <Shimmer className="h-10 w-20 rounded-xl" />
        </div>
        <div className="space-y-5">
          {[1, 2].map((i) => (
            <div key={i} className="space-y-3">
              <div className="flex gap-2 items-center">
                <Shimmer className="h-10 w-10 rounded-xl shrink-0" />
                <div className="flex-1 space-y-1">
                  <Shimmer className="h-5 w-36 rounded" />
                  <Shimmer className="h-3 w-20 rounded" />
                </div>
              </div>
              <div className="grid gap-3">
                {[1, 2].map((j) => (
                  <div key={j} className="flex gap-3 p-3 rounded-xl border border-gray-200">
                    <Shimmer className="h-16 w-20 rounded-lg shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Shimmer className="h-4 w-3/4 rounded" />
                      <Shimmer className="h-3 w-1/2 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "explore") {
    return (
      <div className="max-w-6xl mx-auto px-2 sm:px-3 space-y-4 animate-pulse">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-2.5">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex rounded-xl border border-gray-100 overflow-hidden bg-white">
              <Shimmer className="w-[6.25rem] sm:w-28 shrink-0 min-h-[5.75rem] self-stretch rounded-none" />
              <div className="flex-1 p-2.5 sm:p-3 space-y-2 min-w-0">
                <Shimmer className="h-4 w-[85%] max-w-[14rem] rounded" />
                <Shimmer className="h-3 w-3/5 rounded" />
                <Shimmer className="h-6 w-full rounded-md" />
                <Shimmer className="h-6 w-20 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "settings") {
    return (
      <div className="max-w-lg mx-auto p-4 space-y-6 animate-pulse">
        <Shimmer className="h-8 w-40 rounded" />
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="p-4 rounded-2xl border border-gray-100 bg-white">
            <div className="flex gap-3 mb-3">
              <Shimmer className="h-10 w-10 rounded-xl shrink-0" />
              <Shimmer className="h-5 w-32 rounded flex-1" />
            </div>
            <Shimmer className="h-4 w-full rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (variant === "order") {
    return (
      <div className="max-w-lg mx-auto p-4 space-y-6 animate-pulse">
        <div className="flex justify-between items-center">
          <Shimmer className="h-8 w-36 rounded" />
          <Shimmer className="h-8 w-20 rounded" />
        </div>
        <div className="rounded-2xl border border-gray-100 p-6 space-y-4">
          <Shimmer className="h-5 w-3/4 rounded" />
          <Shimmer className="h-4 w-1/2 rounded" />
          <div className="pt-4 border-t border-gray-100 space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex justify-between">
                <Shimmer className="h-4 w-24" />
                <Shimmer className="h-4 w-16" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (variant === "selection") {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6 animate-pulse">
        <div className="space-y-3">
          <Shimmer className="h-8 w-3/4 rounded" />
          <Shimmer className="h-4 w-full rounded" />
          <Shimmer className="h-6 w-20 rounded" />
        </div>
        <div className="space-y-5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-4 rounded-xl border border-gray-200 space-y-3">
              <Shimmer className="h-5 w-36 rounded" />
              <Shimmer className="h-3 w-48 rounded" />
              <div className="flex flex-wrap gap-2 pt-2">
                {[1, 2, 3, 4].map((j) => (
                  <Shimmer key={j} className="h-10 w-24 rounded-lg" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "menu") {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-2 space-y-6 animate-pulse">
        <div className="flex justify-between items-center">
          <Shimmer className="h-8 w-48 rounded" />
          <Shimmer className="h-10 w-10 rounded-xl shrink-0" />
        </div>
        <div className="space-y-8">
          {[1, 2, 3].map((cat) => (
            <div key={cat}>
              <Shimmer className="h-6 w-32 rounded mb-4" />
              <div className="grid gap-4 sm:grid-cols-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex gap-4 p-4 rounded-xl border border-gray-200">
                    <Shimmer className="h-20 w-24 rounded-lg shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Shimmer className="h-5 w-3/4 rounded" />
                      <Shimmer className="h-4 w-1/2 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "cart") {
    return (
      <div className="mx-auto max-w-lg space-y-5 animate-pulse">
        <div className="space-y-2">
          <Shimmer className="h-8 w-32 rounded-lg" />
          <Shimmer className="h-4 w-24 rounded" />
        </div>
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-soft">
              <div className="space-y-3 p-4">
                <div className="flex justify-between gap-3">
                  <div className="flex-1 space-y-2">
                    <Shimmer className="h-5 w-3/4 rounded" />
                    <Shimmer className="h-3 w-1/2 rounded" />
                  </div>
                  <Shimmer className="h-6 w-16 rounded" />
                </div>
                <div className="flex gap-2">
                  <Shimmer className="h-6 w-16 rounded-full" />
                  <Shimmer className="h-6 w-20 rounded-full" />
                </div>
              </div>
              <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50/70 px-4 py-3">
                <Shimmer className="h-9 w-28 rounded-xl" />
                <Shimmer className="h-9 w-24 rounded-xl" />
              </div>
            </div>
          ))}
        </div>
        <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-soft p-4 space-y-3">
          <Shimmer className="h-4 w-28 rounded" />
          <div className="flex justify-between">
            <Shimmer className="h-4 w-20 rounded" />
            <Shimmer className="h-4 w-14 rounded" />
          </div>
          <div className="flex justify-between border-t border-slate-100 pt-3">
            <Shimmer className="h-6 w-20 rounded" />
            <Shimmer className="h-6 w-24 rounded" />
          </div>
        </div>
      </div>
    );
  }

  // default: generic page
  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6 animate-pulse">
      <div className="flex justify-between items-center">
        <Shimmer className="h-8 w-40 rounded" />
        <Shimmer className="h-10 w-24 rounded-xl shrink-0" />
      </div>
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4 rounded-xl border border-gray-200 space-y-3">
            <Shimmer className="h-5 w-3/4 rounded" />
            <Shimmer className="h-4 w-1/2 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PageSkeleton;
